/**
 * Greenlight Dashboard V2 — CSS Grid card layout replacing tab-based navigation.
 * Main orchestrator that displays all analysis results in a cinematic card grid.
 *
 * Layout: 3-column CSS Grid with hero card, preview cards, and deep-dive overlay.
 * Uses Screening Room design system tokens (--sr-*) throughout.
 */
import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SRCard from './screening-room/SRCard';
import SRScoreRing from './screening-room/SRScoreRing';
import useAnimatedCounter from './screening-room/useAnimatedCounter';
import WritingCraftRadar from './WritingCraftRadar';
import EmotionalHeatmap from './EmotionalHeatmap';
import CharacterConstellation from './CharacterConstellation';
import CharacterArcAnalysis from './CharacterArcAnalysis';
import DeadZoneDetector from './DeadZoneDetector';
import BudgetTimeline from './BudgetTimeline';
import RiskAssessment from './RiskAssessment';
import MasterTimeline from './MasterTimeline';
import AudienceParliament from './AudienceParliament';
import ComparableFilms from './ComparableFilms';
import PitchDeckGenerator from './PitchDeckGenerator';
import {
  ArrowLeft, Sparkles, AlertTriangle, TrendingUp, Target, Film,
  DollarSign, Shield, Clock, Users, BookOpen, Layers, BarChart3, Zap,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function scoreColor(score) {
  if (score >= 85) return 'var(--sr-score-greenlit)';
  if (score >= 70) return 'var(--sr-score-high)';
  if (score >= 50) return 'var(--sr-score-mid)';
  return 'var(--sr-score-low)';
}

function scoreTierLabel(score) {
  if (score >= 85) return '1895 GREENLIT';
  if (score >= 70) return 'PROMISING';
  if (score >= 50) return 'DEVELOP FURTHER';
  return 'PASS';
}

function badgeClass(score) {
  if (score >= 85) return 'greenlit';
  if (score >= 70) return 'high';
  if (score >= 50) return 'mid';
  return 'low';
}

/* ------------------------------------------------------------------ */
/*  Sub-score bar for the hero card                                    */
/* ------------------------------------------------------------------ */

function SubScoreBar({ label, score, isFocusArea, delay = 0 }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 4,
      }}>
        <span style={{
          fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-caption)',
          color: isFocusArea ? 'var(--sr-critical)' : 'var(--sr-text-secondary)',
          fontWeight: isFocusArea ? 600 : 400,
        }}>
          {label}
          {isFocusArea && (
            <span style={{
              marginLeft: 8, fontSize: 'var(--sr-text-micro)',
              color: 'var(--sr-critical)', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Focus Area
            </span>
          )}
        </span>
        <span style={{
          fontFamily: 'var(--sr-font-mono)', fontSize: 'var(--sr-text-caption)',
          color: scoreColor(score), fontWeight: 600,
        }}>
          {score}
        </span>
      </div>
      <div style={{
        height: 6, borderRadius: 3, background: 'var(--sr-border-subtle)',
        overflow: 'hidden', position: 'relative',
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 1.2, delay: delay / 1000, ease: [0.22, 1, 0.36, 1] }}
          style={{
            height: '100%', borderRadius: 3,
            background: scoreColor(score),
          }}
        />
        {isFocusArea && (
          <div style={{
            position: 'absolute', bottom: -3, left: 0, right: 0,
            height: 2, background: 'var(--sr-critical)', borderRadius: 1,
          }} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Metadata pill for hero card bottom strip                           */
/* ------------------------------------------------------------------ */

function MetadataPill({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 100,
      background: 'var(--sr-surface-overlay)',
      border: '1px solid var(--sr-border-subtle)',
      fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-micro)',
      color: 'var(--sr-text-tertiary)',
    }}>
      {Icon && <Icon size={12} />}
      <span>{label}:</span>
      <span style={{ color: 'var(--sr-text-secondary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero Card                                                          */
/* ------------------------------------------------------------------ */

function HeroCard({ analysis, onClick }) {
  const {
    greenlight_score = 0, badge, sub_scores = {}, executive_summary = {},
  } = analysis;

  const subScoreEntries = useMemo(() => {
    const entries = [
      { key: 'writing_craft', label: 'Writing', score: sub_scores.writing_craft },
      { key: 'character_arc', label: 'Character', score: sub_scores.character_arc },
      { key: 'emotional_architecture', label: 'Emotion', score: sub_scores.emotional_architecture },
      { key: 'narrative_logic', label: 'Logic', score: sub_scores.narrative_logic },
      { key: 'audience_breadth', label: 'Audience', score: sub_scores.audience_breadth },
      { key: 'producibility', label: 'Budget', score: sub_scores.producibility },
    ].filter(e => e.score !== undefined && e.score !== null);
    return entries;
  }, [sub_scores]);

  const weakestDim = useMemo(() => {
    if (!subScoreEntries.length) return null;
    return subScoreEntries.reduce((min, e) => (e.score < min.score ? e : min), subScoreEntries[0]);
  }, [subScoreEntries]);

  const pageCount = analysis.page_count || analysis.metadata?.page_count;
  const sceneCount = analysis.scene_count || analysis.metadata?.scene_count;
  const charCount = analysis.character_count || analysis.characters?.length;
  const processingTime = analysis.processing_time || analysis.metadata?.processing_time;

  return (
    <div
      className="sr-card sr-card--hero"
      style={{ gridColumn: '1 / -1', cursor: 'default' }}
    >
      <div style={{
        display: 'flex', gap: 40, alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}>
        {/* Left: Score Ring */}
        <div style={{
          flex: '0 0 auto', display: 'flex', flexDirection: 'column',
          alignItems: 'center', minWidth: 200,
        }}>
          <SRScoreRing score={greenlight_score} size={200} strokeWidth={8} delay={400} showBadge={true} />
        </div>

        {/* Right: Verdict + Sub-scores */}
        <div style={{ flex: 1, minWidth: 280 }}>
          {executive_summary?.one_line_verdict && (
            <p style={{
              fontFamily: 'var(--sr-font-display)',
              fontSize: 'var(--sr-text-subheading)',
              fontWeight: 600,
              color: 'var(--sr-text-primary)',
              lineHeight: 1.4,
              marginBottom: 20,
              marginTop: 0,
            }}>
              {executive_summary.one_line_verdict}
            </p>
          )}

          <div style={{ maxWidth: 460 }}>
            {subScoreEntries.map((entry, i) => (
              <SubScoreBar
                key={entry.key}
                label={entry.label}
                score={entry.score}
                isFocusArea={weakestDim?.key === entry.key}
                delay={800 + i * 100}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom metadata strip */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        marginTop: 24, paddingTop: 16,
        borderTop: '1px solid var(--sr-border-subtle)',
      }}>
        <MetadataPill icon={BookOpen} label="Pages" value={pageCount} />
        <MetadataPill icon={Film} label="Scenes" value={sceneCount} />
        <MetadataPill icon={Users} label="Characters" value={charCount} />
        <MetadataPill icon={Clock} label="Processing" value={processingTime ? `${Math.round(processingTime)}s` : null} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Dead Zones Preview                                                 */
/* ------------------------------------------------------------------ */

function DeadZonesPreview({ data }) {
  if (!data) return <NoData />;
  const zones = data.dead_zones || data.zones || [];
  const count = zones.length;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
      }}>
        <span style={{
          fontFamily: 'var(--sr-font-mono)', fontSize: 24, fontWeight: 700,
          color: count > 3 ? 'var(--sr-critical)' : count > 0 ? 'var(--sr-warning)' : 'var(--sr-success)',
        }}>
          {count}
        </span>
        <span style={{
          fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-caption)',
          color: 'var(--sr-text-tertiary)',
        }}>
          dead zone{count !== 1 ? 's' : ''} detected
        </span>
      </div>

      {/* Timeline stripe */}
      <div style={{
        height: 8, borderRadius: 4, background: 'var(--sr-border-subtle)',
        position: 'relative', overflow: 'hidden',
      }}>
        {zones.map((z, i) => {
          const start = (z.start_page || z.start || 0) / (data.total_pages || 120) * 100;
          const end = (z.end_page || z.end || 0) / (data.total_pages || 120) * 100;
          return (
            <div key={i} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${start}%`, width: `${Math.max(end - start, 2)}%`,
              background: 'rgba(113, 113, 122, 0.5)', borderRadius: 2,
            }} />
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Logic Preview                                                      */
/* ------------------------------------------------------------------ */

function LogicPreview({ data }) {
  if (!data) return <NoData />;
  const issues = data.issues || [];
  const critical = issues.filter(i => i.severity === 'critical').length;
  const moderate = issues.filter(i => i.severity === 'moderate').length;
  const minor = issues.filter(i => i.severity === 'minor').length;
  const threads = data.plot_threads || [];
  const unresolved = threads.filter(t => !t.resolved).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {critical > 0 && <SeverityBadge count={critical} color="var(--sr-critical)" label="Critical" />}
        {moderate > 0 && <SeverityBadge count={moderate} color="var(--sr-warning)" label="Moderate" />}
        {minor > 0 && <SeverityBadge count={minor} color="var(--sr-text-tertiary)" label="Minor" />}
      </div>
      {threads.length > 0 && (
        <div style={{
          fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-micro)',
          color: 'var(--sr-text-tertiary)', marginTop: 8,
        }}>
          {threads.length} plot thread{threads.length !== 1 ? 's' : ''} tracked
          {unresolved > 0 && (
            <span style={{ color: 'var(--sr-warning)', marginLeft: 4 }}>
              ({unresolved} unresolved)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SeverityBadge({ count, color, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 100,
      background: `${color}15`, border: `1px solid ${color}30`,
      fontFamily: 'var(--sr-font-mono)', fontSize: 'var(--sr-text-micro)',
      color, fontWeight: 600,
    }}>
      {count} {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Budget Preview                                                     */
/* ------------------------------------------------------------------ */

function BudgetPreview({ data }) {
  if (!data) return <NoData />;
  const tier = data.budget_tier || data.tier || 'Unknown';
  const estimate = data.estimated_budget || data.total_estimate;
  const breakdown = data.cost_breakdown || data.breakdown || [];

  return (
    <div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '4px 12px', borderRadius: 100,
        background: 'var(--sr-gold-glow)', border: '1px solid rgba(201,168,76,0.25)',
        fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-caption)',
        color: 'var(--sr-gold-bright)', fontWeight: 600, marginBottom: 12,
      }}>
        <DollarSign size={12} />
        {tier}
      </div>
      {estimate && (
        <div style={{
          fontFamily: 'var(--sr-font-mono)', fontSize: 'var(--sr-text-body)',
          color: 'var(--sr-text-primary)', fontWeight: 600, marginBottom: 8,
        }}>
          {typeof estimate === 'number' ? `$${(estimate / 1_000_000).toFixed(1)}M` : estimate}
        </div>
      )}
      {/* Mini bar chart */}
      {breakdown.slice(0, 4).map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
        }}>
          <span style={{
            flex: '0 0 60px', fontFamily: 'var(--sr-font-body)',
            fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-tertiary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.category || item.label}
          </span>
          <div style={{
            flex: 1, height: 4, borderRadius: 2, background: 'var(--sr-border-subtle)',
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'var(--sr-gold-dim)',
              width: `${item.percentage || (item.amount / (estimate || 1) * 100)}%`,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cross-Module Insights Preview                                      */
/* ------------------------------------------------------------------ */

function CrossModulePreview({ data }) {
  if (!data || !data.length) return <NoData label="No cross-module insights yet" />;
  const insights = Array.isArray(data) ? data : data.insights || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {insights.slice(0, 5).map((insight, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '8px 12px', borderRadius: 10,
          background: 'var(--sr-surface-overlay)',
          border: '1px solid var(--sr-border-subtle)',
        }}>
          <Zap size={12} style={{ color: 'var(--sr-gold)', marginTop: 2, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{
              fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-micro)',
              color: 'var(--sr-text-secondary)', margin: 0, lineHeight: 1.4,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {typeof insight === 'string' ? insight : insight.text || insight.description || insight.insight}
            </p>
            {insight.modules && (
              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                {(Array.isArray(insight.modules) ? insight.modules : [insight.modules]).map((m, j) => (
                  <span key={j} style={{
                    padding: '1px 6px', borderRadius: 4,
                    background: 'var(--sr-gold-glow)',
                    fontFamily: 'var(--sr-font-mono)', fontSize: 9,
                    color: 'var(--sr-gold)', textTransform: 'uppercase',
                  }}>
                    {m}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Quick Actions Card                                                 */
/* ------------------------------------------------------------------ */

function QuickActionsCard({ analysis }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[
        { icon: Sparkles, label: 'Generate Pitch Deck', color: 'var(--sr-gold)' },
        { icon: BarChart3, label: 'Export Report', color: 'var(--sr-info)' },
        { icon: Layers, label: 'Compare to Previous', color: 'var(--sr-text-tertiary)' },
      ].map(({ icon: Icon, label, color }) => (
        <button
          key={label}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 16px', borderRadius: 10,
            background: 'var(--sr-surface-overlay)',
            border: '1px solid var(--sr-border-subtle)',
            cursor: 'pointer', transition: 'all 0.2s ease',
            fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-caption)',
            color: 'var(--sr-text-secondary)', fontWeight: 500,
            width: '100%', textAlign: 'left',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--sr-gold-dim)';
            e.currentTarget.style.background = 'var(--sr-surface-elevated)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--sr-border-subtle)';
            e.currentTarget.style.background = 'var(--sr-surface-overlay)';
          }}
        >
          <Icon size={16} style={{ color, flexShrink: 0 }} />
          {label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  No Data fallback                                                   */
/* ------------------------------------------------------------------ */

function NoData({ label = 'No data available' }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: 80, fontFamily: 'var(--sr-font-body)',
      color: 'var(--sr-text-tertiary)', textAlign: 'center',
    }}>
      <span style={{ fontSize: 'var(--sr-text-caption)', fontStyle: 'italic' }}>{label}</span>
      <span style={{ fontSize: 'var(--sr-text-micro)', color: 'var(--sr-border-focus)', marginTop: 4 }}>
        Re-run analysis to generate this data
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Preview wrapper — constrains child to fixed height                 */
/* ------------------------------------------------------------------ */

function PreviewBox({ height = 160, children }) {
  return (
    <div style={{
      height, overflow: 'hidden', position: 'relative',
      borderRadius: 8,
    }}>
      {children}
      {/* Bottom fade to indicate "click to see more" */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 32,
        background: 'linear-gradient(transparent, var(--sr-surface))',
        pointerEvents: 'none', zIndex: 5,
      }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Deep Dive Panel                                                    */
/* ------------------------------------------------------------------ */

function DeepDivePanel({ moduleId, analysis, onClose }) {
  const MODULES = {
    emotions: {
      title: 'Emotional Architecture',
      icon: TrendingUp,
      score: analysis.sub_scores?.emotional_architecture,
      render: () => <EmotionalHeatmap data={analysis.emotional_architecture} />,
    },
    characters: {
      title: 'Character Constellation',
      icon: Users,
      score: analysis.sub_scores?.character_arc,
      render: () => <CharacterConstellation data={analysis.characters} />,
    },
    arcs: {
      title: 'Character Arcs',
      icon: Target,
      score: analysis.sub_scores?.character_arc,
      render: () => <CharacterArcAnalysis data={analysis.character_arcs} />,
    },
    writing: {
      title: 'Writing Craft',
      icon: BookOpen,
      score: analysis.sub_scores?.writing_craft,
      render: () => <WritingCraftRadar data={analysis.writing_craft} />,
    },
    'dead-zones': {
      title: 'Dead Zones',
      icon: AlertTriangle,
      render: () => <DeadZoneDetector data={analysis.dead_zones} />,
    },
    audience: {
      title: 'Audience Parliament',
      icon: Users,
      score: analysis.sub_scores?.audience_breadth,
      render: () => <AudienceParliament data={{
        audience_breadth: analysis.commercial?.audience_breadth,
        audience_segmentation: analysis.audience_segmentation,
      }} />,
    },
    logic: {
      title: 'Narrative Logic',
      icon: Layers,
      score: analysis.sub_scores?.narrative_logic,
      render: () => <LogicFullPanel data={analysis.narrative_logic} />,
    },
    budget: {
      title: 'Budget Estimate',
      icon: DollarSign,
      score: analysis.sub_scores?.producibility,
      render: () => <BudgetTimeline data={analysis.budget} />,
    },
    risk: {
      title: 'Risk Assessment',
      icon: Shield,
      render: () => <RiskAssessment data={analysis.risk} />,
    },
    timeline: {
      title: 'Master Timeline',
      icon: Clock,
      render: () => <MasterTimeline analysis={analysis} />,
    },
    'cross-module': {
      title: 'Cross-Module Insights',
      icon: Zap,
      render: () => <CrossModuleFullPanel data={analysis.cross_module_insights} />,
    },
    'quick-actions': {
      title: 'Quick Actions',
      icon: Sparkles,
      render: () => (
        <div style={{ maxWidth: 600 }}>
          <PitchDeckGenerator analysis={analysis} />
        </div>
      ),
    },
    comps: {
      title: 'Comparable Films',
      icon: Film,
      render: () => <ComparableFilms data={analysis.comparable_films} />,
    },
  };

  const mod = MODULES[moduleId];
  if (!mod) return null;
  const Icon = mod.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 1100, margin: '40px auto', padding: '0 24px 60px',
          minHeight: '80vh',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          marginBottom: 32, paddingTop: 8,
        }}>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 10,
              background: 'var(--sr-surface-elevated)',
              border: '1px solid var(--sr-border-subtle)',
              cursor: 'pointer', color: 'var(--sr-text-secondary)',
              fontFamily: 'var(--sr-font-body)', fontSize: 'var(--sr-text-caption)',
              fontWeight: 500, transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--sr-gold-dim)';
              e.currentTarget.style.color = 'var(--sr-gold)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--sr-border-subtle)';
              e.currentTarget.style.color = 'var(--sr-text-secondary)';
            }}
          >
            <ArrowLeft size={14} />
            Back to Dashboard
          </button>

          <div style={{ flex: 1 }} />

          <Icon size={18} style={{ color: 'var(--sr-gold)' }} />
          <span style={{
            fontFamily: 'var(--sr-font-display)', fontSize: 'var(--sr-text-heading)',
            fontWeight: 600, color: 'var(--sr-text-primary)',
          }}>
            {mod.title}
          </span>
          {mod.score !== undefined && mod.score !== null && (
            <span className={`sr-score-pill sr-score-pill--${badgeClass(mod.score)}`}>
              {mod.score}
            </span>
          )}
        </div>

        {/* Full module */}
        <div className="sr-card" style={{ padding: 32, borderRadius: 18 }}>
          {mod.render()}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Logic Full Panel (reused from V1)                                  */
/* ------------------------------------------------------------------ */

function LogicFullPanel({ data }) {
  if (!data) return <NoData />;
  const issues = data.issues || [];
  const threads = data.plot_threads || [];

  return (
    <div style={{ fontFamily: 'var(--sr-font-body)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20,
      }}>
        <span style={{ fontSize: 'var(--sr-text-body)', color: 'var(--sr-text-primary)', fontWeight: 600 }}>
          Narrative Logic
        </span>
        <span style={{
          fontFamily: 'var(--sr-font-mono)', fontSize: 'var(--sr-text-heading)',
          color: scoreColor(data.logic_score), fontWeight: 700,
        }}>
          {data.logic_score}/100
        </span>
      </div>

      {/* Severity counts */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <SeverityBadge count={issues.filter(i => i.severity === 'critical').length} color="var(--sr-critical)" label="Critical" />
        <SeverityBadge count={issues.filter(i => i.severity === 'moderate').length} color="var(--sr-warning)" label="Moderate" />
        <SeverityBadge count={issues.filter(i => i.severity === 'minor').length} color="var(--sr-text-tertiary)" label="Minor" />
      </div>

      {/* Issues */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {issues.map((issue, i) => {
          const col = issue.severity === 'critical' ? 'var(--sr-critical)'
            : issue.severity === 'moderate' ? 'var(--sr-warning)' : 'var(--sr-text-tertiary)';
          return (
            <div key={i} style={{
              padding: 14, borderRadius: 12,
              background: `${col}08`, border: `1px solid ${col}25`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <AlertTriangle size={12} style={{ color: col }} />
                <span style={{ fontSize: 'var(--sr-text-caption)', fontWeight: 600, color: 'var(--sr-text-primary)' }}>
                  {issue.type?.replace(/_/g, ' ')}
                </span>
                <span style={{ fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-tertiary)' }}>
                  {issue.location}
                </span>
              </div>
              <p style={{ fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-secondary)', margin: 0, lineHeight: 1.5 }}>
                {issue.description}
              </p>
              {issue.suggested_fix && (
                <p style={{ fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-tertiary)', margin: '6px 0 0', fontStyle: 'italic' }}>
                  {issue.suggested_fix}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Plot threads */}
      {threads.length > 0 && (
        <>
          <h4 style={{ fontSize: 'var(--sr-text-caption)', color: 'var(--sr-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Plot Threads
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {threads.map((t, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', borderRadius: 8,
                background: 'var(--sr-surface-overlay)',
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: t.resolved ? 'var(--sr-success)' : 'var(--sr-critical)',
                  flexShrink: 0,
                }} />
                <span style={{ flex: 1, fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-secondary)' }}>
                  {t.thread}
                </span>
                <span style={{ fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-tertiary)' }}>
                  {t.resolved ? 'Resolved' : 'Open'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {data.structural_assessment && (
        <div style={{
          marginTop: 20, padding: 14, borderRadius: 12,
          background: 'var(--sr-surface-elevated)',
          border: '1px solid var(--sr-border-subtle)',
          fontSize: 'var(--sr-text-micro)', color: 'var(--sr-text-secondary)', lineHeight: 1.6,
        }}>
          {data.structural_assessment}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cross-Module Full Panel                                            */
/* ------------------------------------------------------------------ */

function CrossModuleFullPanel({ data }) {
  if (!data) return <NoData label="No cross-module insights generated" />;
  const insights = Array.isArray(data) ? data : data.insights || [];

  return (
    <div style={{ fontFamily: 'var(--sr-font-body)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {insights.map((insight, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: 16, borderRadius: 12,
            background: 'var(--sr-surface-overlay)',
            border: '1px solid var(--sr-border-subtle)',
          }}>
            <Zap size={14} style={{ color: 'var(--sr-gold)', marginTop: 2, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{
                fontSize: 'var(--sr-text-caption)', color: 'var(--sr-text-primary)',
                margin: 0, lineHeight: 1.5, fontWeight: 500,
              }}>
                {typeof insight === 'string' ? insight : insight.text || insight.description || insight.insight}
              </p>
              {insight.modules && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {(Array.isArray(insight.modules) ? insight.modules : [insight.modules]).map((m, j) => (
                    <span key={j} style={{
                      padding: '2px 8px', borderRadius: 6,
                      background: 'var(--sr-gold-glow)',
                      fontFamily: 'var(--sr-font-mono)', fontSize: 10,
                      color: 'var(--sr-gold)', textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {m}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Card definitions                                                   */
/* ------------------------------------------------------------------ */

function useCardDefinitions(analysis) {
  return useMemo(() => {
    const {
      emotional_architecture, characters, writing_craft, dead_zones,
      audience_segmentation, narrative_logic, budget, risk, commercial,
      cross_module_insights, sub_scores = {},
    } = analysis;

    return [
      {
        id: 'emotions',
        title: 'Emotional Architecture',
        subtitle: 'Emotion heatmap across narrative',
        icon: TrendingUp,
        score: sub_scores.emotional_architecture,
        size: 'large',
        row: 1,
        hasData: !!emotional_architecture,
        preview: (
          <PreviewBox height={180}>
            <EmotionalHeatmap data={emotional_architecture} preview />
          </PreviewBox>
        ),
      },
      {
        id: 'characters',
        title: 'Character Constellation',
        subtitle: 'Relationships & depth',
        icon: Users,
        score: sub_scores.character_arc,
        size: 'medium',
        row: 1,
        hasData: !!characters,
        preview: (
          <PreviewBox height={200}>
            <CharacterConstellation data={characters} preview />
          </PreviewBox>
        ),
      },
      {
        id: 'writing',
        title: 'Writing Craft',
        subtitle: '10-axis radar analysis',
        icon: BookOpen,
        score: sub_scores.writing_craft,
        size: 'standard',
        row: 2,
        hasData: !!writing_craft,
        preview: (
          <PreviewBox height={140}>
            <WritingCraftRadar data={writing_craft} preview />
          </PreviewBox>
        ),
      },
      {
        id: 'dead-zones',
        title: 'Dead Zones',
        subtitle: 'Engagement drop detection',
        icon: AlertTriangle,
        size: 'standard',
        row: 2,
        hasData: !!dead_zones,
        preview: <DeadZonesPreview data={dead_zones} />,
      },
      {
        id: 'audience',
        title: 'Audience Parliament',
        subtitle: 'Segment analysis',
        icon: Users,
        score: sub_scores.audience_breadth,
        size: 'medium',
        row: 2,
        tall: true,
        hasData: !!(audience_segmentation || commercial?.audience_breadth),
        preview: (
          <PreviewBox height={220}>
            <AudienceParliament
              data={{
                audience_breadth: commercial?.audience_breadth,
                audience_segmentation,
              }}
              preview
            />
          </PreviewBox>
        ),
      },
      {
        id: 'logic',
        title: 'Logic & Plot Holes',
        subtitle: 'Structural integrity',
        icon: Layers,
        score: sub_scores.narrative_logic,
        size: 'standard',
        row: 3,
        hasData: !!narrative_logic,
        preview: <LogicPreview data={narrative_logic} />,
      },
      {
        id: 'budget',
        title: 'Budget Estimate',
        subtitle: 'Cost analysis',
        icon: DollarSign,
        score: sub_scores.producibility,
        size: 'standard',
        row: 3,
        hasData: !!budget,
        preview: <BudgetPreview data={budget} />,
      },
      {
        id: 'risk',
        title: 'Risk Assessment',
        subtitle: 'Production risk matrix',
        icon: Shield,
        size: 'standard',
        row: 3,
        hasData: !!risk,
        preview: (
          <PreviewBox height={140}>
            <RiskAssessment data={risk} preview />
          </PreviewBox>
        ),
      },
      {
        id: 'comps',
        title: 'Comparable Films',
        subtitle: 'Market intelligence',
        icon: Film,
        size: 'large',
        row: 4,
        hasData: !!analysis.comparable_films,
        preview: (
          <PreviewBox height={160}>
            <ComparableFilms data={analysis.comparable_films} />
          </PreviewBox>
        ),
      },
      {
        id: 'timeline',
        title: 'Master Timeline',
        subtitle: 'Full narrative arc',
        icon: Clock,
        size: 'large',
        fullWidth: true,
        row: 5,
        hasData: true,
        preview: (
          <PreviewBox height={120}>
            <MasterTimeline analysis={analysis} preview />
          </PreviewBox>
        ),
      },
      {
        id: 'cross-module',
        title: 'Cross-Module Insights',
        subtitle: 'AI-generated connections',
        icon: Zap,
        size: 'large',
        row: 6,
        hasData: !!cross_module_insights,
        preview: <CrossModulePreview data={cross_module_insights} />,
      },
      {
        id: 'quick-actions',
        title: 'Quick Actions',
        subtitle: 'Export & generate',
        icon: Sparkles,
        size: 'medium',
        row: 6,
        hasData: true,
        preview: <QuickActionsCard analysis={analysis} />,
      },
    ];
  }, [analysis]);
}

/* ------------------------------------------------------------------ */
/*  Stagger animation config                                           */
/* ------------------------------------------------------------------ */

const ROW_DELAYS = { 1: 0.2, 2: 0.4, 3: 0.6, 4: 0.8, 5: 1.0, 6: 1.2 };

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (row) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: ROW_DELAYS[row] || 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

/* ------------------------------------------------------------------ */
/*  Grid size mapping — CSS Grid column spans                          */
/* ------------------------------------------------------------------ */

function gridColumnStyle(card) {
  if (card.fullWidth) return { gridColumn: '1 / -1' };
  if (card.size === 'large') return { gridColumn: 'span 2' };
  return {};
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function GreenlightDashboardV2({ analysis }) {
  const [activeDeepDive, setActiveDeepDive] = useState(null);

  const cards = useCardDefinitions(analysis || {});

  const handleCardClick = useCallback((id) => {
    setActiveDeepDive(id);
  }, []);

  const handleCloseDeepDive = useCallback(() => {
    setActiveDeepDive(null);
  }, []);

  if (!analysis) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ background: 'var(--sr-deep)' }}
    >
      <div className="sr-dashboard-grid">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ gridColumn: '1 / -1' }}
        >
          <HeroCard analysis={analysis} />
        </motion.div>

        {/* Module Cards */}
        {cards.map((card) => {
          if (!card.hasData && card.id !== 'quick-actions') return null;

          return (
            <motion.div
              key={card.id}
              custom={card.row}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              style={gridColumnStyle(card)}
            >
              <SRCard
                size={card.size}
                title={card.title}
                subtitle={card.subtitle}
                score={card.score}
                onClick={() => handleCardClick(card.id)}
                className={card.tall ? 'sr-card--tall' : ''}
              >
                {card.preview}
              </SRCard>
            </motion.div>
          );
        })}
      </div>

      {/* Deep Dive Overlay */}
      <AnimatePresence>
        {activeDeepDive && (
          <DeepDivePanel
            moduleId={activeDeepDive}
            analysis={analysis}
            onClose={handleCloseDeepDive}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
